import { useEffect, useRef, useState } from 'react';
import type { ProductLine } from '@popcart/core/v1';
import { ProductArt } from '../art';
import { Icon } from '../../ui/Icon';
import { FieldError, fid } from './fields';
import { PHOTO_NOTE, PUBLISH_ON_PHOTO } from '../lib/photo-rule.js';

/**
 * Photo (required, 0013) with preview and replace. `onPick` gets the raw file; the editor shrinks
 * and uploads it (right away on an existing product, right after create on a new one). Customers
 * see the photo on the product page; the §10 drawing stays the thumbnail on cards and lists, so it
 * shows here too, labelled as the thumbnail.
 */
export function PhotoField({
  imageUrl,
  pending,
  busy,
  error,
  onPick,
  onClear,
  name,
  line,
  category,
  missing = false,
  publishOnPhoto = false,
  step = false,
}: {
  name: string;
  line: ProductLine;
  category?: string | null;
  imageUrl: string | null;
  pending: File | null;
  busy: boolean;
  error?: string;
  onPick(f: File): void;
  onClear?(): void;
  /** Saved product without a real photo yet (AdminProduct.needsPhoto). */
  missing?: boolean;
  /** It goes on sale as soon as the photo lands. */
  publishOnPhoto?: boolean;
  /** Just created: this is the next step; draw attention to it. */
  step?: boolean;
}) {
  const [preview, setPreview] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!pending) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(pending);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [pending]);
  const src = preview ?? imageUrl;
  const waiting = missing && !src;

  return (
    <section className={`card panel cat-photo-sec${waiting ? ' needs' : ''}${step && waiting ? ' step' : ''}`} aria-labelledby="sec-photo" data-photo-step={step && waiting ? 'true' : undefined}>
      <h2 id="sec-photo">Photo (required)</h2>
      <p className="t-caption muted">{PHOTO_NOTE}</p>
      {waiting ? (
        <p className="note cat-photo-why" role="status">
          <span className="t-label">{publishOnPhoto ? PUBLISH_ON_PHOTO : 'Add a photo to put it on sale.'}</span>
        </p>
      ) : null}
      <label className={`cat-photo${src ? ' has' : ''}`} data-invalid={error ? 'true' : undefined} aria-busy={busy || undefined}>
        <input
          ref={input}
          id={fid('photo')}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/*"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${fid('photo')}-err` : 'photo-help'}
          disabled={busy}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onPick(f);
            e.target.value = '';
          }}
        />
        {src ? (
          <img src={src} alt="Product photo preview" data-photo="preview" />
        ) : (
          <span className="stack s4 cat-photo-empty">
            <Icon name="plus" />
            <span className="t-label">Add a photo</span>
            <span className="t-caption muted">A real photo of the product, taken in good light.</span>
          </span>
        )}
      </label>
      <span className="row cat-photo-thumb">
        <ProductArt className="cat-thumb" name={name} line={line} category={category} />
        <span className="t-caption muted">Thumbnail on cards and lists: the drawing.</span>
      </span>
      <span id="photo-help" className="t-caption muted">
        {busy ? 'Uploading…' : pending ? 'Uploads when you save.' : 'JPEG, PNG or WebP. Square looks best. Large photos are shrunk before upload.'}
      </span>
      <div className="row wrap">
        {src ? (
          <button type="button" className="btn" disabled={busy} onClick={() => input.current?.click()}>
            Replace photo
          </button>
        ) : null}
        {pending && onClear ? (
          <button type="button" className="btn btn-quiet" disabled={busy} onClick={onClear}>
            Remove
          </button>
        ) : null}
      </div>
      <FieldError name="photo" error={error} />
    </section>
  );
}
